import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useDebounce } from '../hooks/useDebounce';
import { useToastNotifications } from '../components/ui/Toast';
import { apiService } from '../services/api';
import { formatDateOnly } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  Eye,
  Shield,
  BarChart3,
  Users,
  DollarSign,
  Lock,
  Unlock,
  Filter,
  X,
  User,
  Globe,
  Hash,
  FileText
} from 'lucide-react';

interface Reseller {
  id: number;
  username: string;
  password?: string;
  email?: string;
  ip?: string;
  date_registered?: number;
  last_login?: number;
  member_group_id?: number;
  credits?: number;
  notes?: string;
  status: number;
  reseller_dns?: string;
  owner_id?: number;
  owner_username?: string | null;
  owner?: {
    id: number;
    username: string | null;
  } | null;
  override_packages?: string;
  hue?: string;
  theme?: number;
  timezone?: string;
  api_key?: string;
  panel?: string;
  group?: {
    group_id: number;
    group_name: string;
    group_color: string;
    is_admin: number;
    is_reseller: number;
  };
}

const Resellers: React.FC = () => {
  const { isAdmin } = useAuth();
  const { showSuccess, showError } = useToastNotifications();
  const [resellers, setResellers] = useState<Reseller[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [ownerIdFilter, setOwnerIdFilter] = useState<string>('');
  const [ownerNameFilter, setOwnerNameFilter] = useState<string>('');
  const [searchMode, setSearchMode] = useState<'normal' | 'owner'>('normal');
  const [ownerSuggestions, setOwnerSuggestions] = useState<Array<{id: number; username: string}>>([]);
  const [showOwnerSuggestions, setShowOwnerSuggestions] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    suspended: 0
  });
  
  // Debounce para busca (500ms de delay) - mesma lógica dos clientes
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const debouncedOwnerSearch = useDebounce(ownerNameFilter, 300);
  
  // Ref para manter foco no input de busca
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  const [selectedReseller, setSelectedReseller] = useState<Reseller | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewModalActiveClientsData, setViewModalActiveClientsData] = useState<{
    active_clients_tree: number;
    active_clients_tree_with_trials: number;
    active_iptv: number;
    active_iptv_with_trials: number;
    active_p2p: number;
    active_p2p_with_trials: number;
  } | null>(null);
  const [viewModalIncludeTrials, setViewModalIncludeTrials] = useState(false);
  const [viewModalLoadingClients, setViewModalLoadingClients] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    username: '',
    email: '',
    password: '',
    cmsPassword: '',
    notes: '',
    panel: '',
    status: 1,
    reseller_dns: '',
    member_group_id: 4
  });
  const [creditsFormData, setCreditsFormData] = useState({
    amount: '',
    reason: ''
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    username: '',
    password: '',
    email: '',
    member_group_id: 4,
    credits: 0,
    reseller_dns: '',
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [showMigrateModal, setShowMigrateModal] = useState(false);
  const [migrateTargets, setMigrateTargets] = useState<Array<{value: number; label: string}>>([]);
  const [migrateTargetId, setMigrateTargetId] = useState<number | null>(null);

  // Buscar sugestões de owners quando estiver no modo owner
  useEffect(() => {
    const fetchOwnerSuggestions = async () => {
      if (searchMode === 'owner' && debouncedOwnerSearch && debouncedOwnerSearch.trim().length >= 2) {
        try {
          const response = await apiService.getResellers({
            search: debouncedOwnerSearch.trim(),
            per: 10
          });
          if (response.data) {
            setOwnerSuggestions(response.data.map((r: any) => ({
              id: r.id,
              username: r.username
            })));
            setShowOwnerSuggestions(true);
          }
        } catch (error) {
          console.error('Erro ao buscar sugestões de owners:', error);
          setOwnerSuggestions([]);
        }
      } else {
        setOwnerSuggestions([]);
        setShowOwnerSuggestions(false);
      }
    };

    fetchOwnerSuggestions();
  }, [debouncedOwnerSearch, searchMode]);

  // Carregar revendedores - mesma lógica dos clientes
  const loadResellers = useCallback(async () => {
    try {
      // Se for uma busca (não o carregamento inicial), mostrar indicador de busca
      const hasSearch = searchMode === 'normal' 
        ? (debouncedSearchTerm && debouncedSearchTerm.trim())
        : (ownerIdFilter && ownerIdFilter.trim());
      
      if (hasSearch || filterStatus !== 'all') {
        setIsSearching(true);
      } else {
        setLoading(true);
      }
      
      setError(null);

      // Construir parâmetros da query
      const params: any = {
        page: pagination.current_page.toString(),
        per: pagination.per_page.toString()
      };

      // Aplicar busca baseada no modo selecionado
      if (searchMode === 'normal') {
        // Busca padrão: por nome, email, username
        if (debouncedSearchTerm && debouncedSearchTerm.trim()) {
          params.search = debouncedSearchTerm.trim();
        }
      } else {
        // Busca por Owner: filtrar por owner_id
        if (ownerIdFilter && ownerIdFilter.trim()) {
          params.owner_id = ownerIdFilter.trim();
        }
      }

      if (filterStatus && filterStatus !== 'all') {
        params.status = filterStatus;
      }

      // Usar apiService
      const data = await apiService.getResellers(params);
      
      setResellers(data.data || []);
      setPagination(data.pagination || {
        current_page: 1,
        per_page: 15,
        total: 0,
        total_pages: 0,
        has_next: false,
        has_prev: false
      });
      setStats(data.stats || {
        total: 0,
        active: 0,
        inactive: 0,
        suspended: 0
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  }, [pagination.current_page, pagination.per_page, debouncedSearchTerm, filterStatus, ownerIdFilter, searchMode]);

  // Carregar revendedores quando mudarem os filtros
  useEffect(() => {
    loadResellers();
  }, [loadResellers]);

  // Buscar clientes ativos na árvore quando abrir o modal de visualização
  useEffect(() => {
    if (showViewModal && selectedReseller?.id) {
      setViewModalActiveClientsData(null);
      setViewModalLoadingClients(true);
      apiService.getResellerActiveClientsTreeCount(selectedReseller.id.toString())
        .then((res) => {
          setViewModalActiveClientsData({
            active_clients_tree: res.active_clients_tree ?? 0,
            active_clients_tree_with_trials: res.active_clients_tree_with_trials ?? 0,
            active_iptv: res.active_iptv ?? 0,
            active_iptv_with_trials: res.active_iptv_with_trials ?? 0,
            active_p2p: res.active_p2p ?? 0,
            active_p2p_with_trials: res.active_p2p_with_trials ?? 0
          });
        })
        .catch(() => {
          setViewModalActiveClientsData(null);
        })
        .finally(() => {
          setViewModalLoadingClients(false);
        });
    }
  }, [showViewModal, selectedReseller?.id]);

  // Os revendedores já vêm filtrados da API
  const filteredResellers = resellers;

  // Handler para mudança de status
  const handleStatusChange = (status: string) => {
    setFilterStatus(status);
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  // Handler para selecionar um owner das sugestões
  const handleSelectOwner = (ownerId: number, ownerUsername: string) => {
    setOwnerIdFilter(ownerId.toString());
    setOwnerNameFilter(ownerUsername);
    setShowOwnerSuggestions(false);
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  // Handler para alternar modo de busca
  const handleSearchModeToggle = () => {
    const newMode = searchMode === 'normal' ? 'owner' : 'normal';
    setSearchMode(newMode);
    // Limpar os campos ao alternar
    if (newMode === 'normal') {
      setOwnerIdFilter('');
      setOwnerNameFilter('');
      setShowOwnerSuggestions(false);
    } else {
      setSearchTerm('');
    }
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  // Handler para mudança no input de owner
  const handleOwnerNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setOwnerNameFilter(value);
    if (!value.trim()) {
      setOwnerIdFilter('');
      setShowOwnerSuggestions(false);
    }
  };

  // Handler para mudança de página
  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, current_page: page }));
  };

  // Funções de gerenciamento de revendedores
  const handleViewReseller = (reseller: Reseller) => {
    setSelectedReseller(reseller);
    setShowViewModal(true);
  };

  const handleEditReseller = (reseller: Reseller) => {
    setSelectedReseller(reseller);
    setEditFormData({
      username: reseller.username,
      email: reseller.email || '',
      password: '',
      cmsPassword: '',
      notes: reseller.notes || '',
      status: reseller.status,
      panel: reseller.panel || '',
      reseller_dns: reseller.reseller_dns || '',
      member_group_id: reseller.member_group_id || 4,
    });
    setShowEditModal(true);
  };

  const handleDeleteReseller = (reseller: Reseller) => {
    setSelectedReseller(reseller);
    setShowDeleteModal(true);
  };

  const handleCreditsReseller = (reseller: Reseller) => {
    setSelectedReseller(reseller);
    setCreditsFormData({
      amount: '',
      reason: ''
    });
    setShowCreditsModal(true);
  };

  const handleBlockReseller = (reseller: Reseller) => {
    setSelectedReseller(reseller);
    setShowBlockModal(true);
  };

  // Função para bloquear/desbloquear revendedor
  const toggleBlockReseller = async (reseller: Reseller, block: boolean) => {
    try {
      if (block) {
        await apiService.blockReseller(reseller.id.toString());
        showSuccess(
          'Revendedor Bloqueado',
          `Revendedor "${reseller.username}" bloqueado com sucesso!`
        );
      } else {
        await apiService.unblockReseller(reseller.id.toString());
        showSuccess(
          'Revendedor Desbloqueado',
          `Revendedor "${reseller.username}" desbloqueado com sucesso!`
        );
      }

      // Recarregar lista
      loadResellers();
      setShowBlockModal(false);
    } catch (err) {
      showError(
        block ? 'Erro ao Bloquear' : 'Erro ao Desbloquear',
        err instanceof Error ? err.message : 'Erro desconhecido'
      );
    }
  };

  // Função para deletar revendedor
  const deleteReseller = async (reseller: Reseller) => {
    try {
      const count = await apiService.getResellerClientsCount(reseller.id.toString());
      if (count.clients > 0) {
        setSelectedReseller(reseller);
        const select = await apiService.getResellerSelectSearch('');
        const options = (select.data || select).filter((r: any) => r.value !== reseller.id).map((r: any) => ({ value: r.value, label: r.label }));
        setMigrateTargets(options);
        setMigrateTargetId(options.length ? options[0].value : null);
        setShowDeleteModal(false);
        setShowMigrateModal(true);
        return;
      }
      await apiService.deleteReseller({ reseller_id: reseller.id });
      
      showSuccess(
        'Revendedor Excluído',
        `Revendedor "${reseller.username}" foi excluído com sucesso!`
      );
      
      loadResellers();
      setShowDeleteModal(false);
    } catch (err) {
      showError(
        'Erro ao Excluir',
        err instanceof Error ? err.message : 'Erro ao excluir revendedor'
      );
    }
  };

  const confirmMigrateAndDelete = async () => {
    try {
      if (!selectedReseller || !migrateTargetId) return;
      
      await apiService.migrateResellerClients(selectedReseller.id.toString(), migrateTargetId);
      await apiService.deleteReseller({ reseller_id: selectedReseller.id });
      
      showSuccess(
        'Migração Concluída',
        `Clientes migrados e revendedor "${selectedReseller.username}" excluído com sucesso!`
      );
      
      setShowMigrateModal(false);
      setSelectedReseller(null);
      loadResellers();
    } catch (err) {
      showError(
        'Erro na Migração',
        err instanceof Error ? err.message : 'Erro ao migrar e excluir revendedor'
      );
    }
  };

  // Função para criar novo revendedor
  const createReseller = async () => {
    try {
      setCreateError(null);
      
      // Validação básica
      if (!createFormData.username || createFormData.username.length < 6) {
        setCreateError('Username deve ter pelo menos 6 caracteres');
        return;
      }
      if (!createFormData.password || createFormData.password.length < 6) {
        setCreateError('Senha deve ter pelo menos 6 caracteres');
        return;
      }
      if (createFormData.email && !createFormData.email.includes('@')) {
        setCreateError('Email inválido');
        return;
      }
      
      // Validação de grupo: não permitir que não-admins criem admins
      if (createFormData.member_group_id === 1 && !isAdmin()) {
        setCreateError('Apenas administradores podem criar usuários do grupo Admin');
        return;
      }

      setCreateLoading(true);
      
      await apiService.createReseller({
        username: createFormData.username,
        password: createFormData.password,
        member_group_id: createFormData.member_group_id,
        reseller_dns: createFormData.reseller_dns,
        credits: createFormData.credits,
        email: createFormData.email || undefined
      });

      // Limpar formulário e fechar modal
      setCreateFormData({ username: '', email: '', member_group_id: 4, password: '', credits: 0, reseller_dns: ''});
      setShowCreateModal(false);
      
      // Mostrar toast de sucesso
      showSuccess(
        'Revendedor Criado',
        `Revendedor "${createFormData.username}" criado com sucesso!`
      );
      
      // Recarregar lista e estatísticas
      loadResellers();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Erro ao criar revendedor');
      showError(
        'Erro ao Criar',
        err instanceof Error ? err.message : 'Erro ao criar revendedor'
      );
    } finally {
      setCreateLoading(false);
    }
  };

  // Função para atualizar revendedor
  const updateReseller = async () => {
    try {
      if (!selectedReseller) {
        throw new Error('Revendedor não selecionado');
      }

      // Validação de grupo: não permitir que não-admins alterem para admin
      if (editFormData.member_group_id === 1 && !isAdmin()) {
        showError('Acesso Negado', 'Apenas administradores podem alterar usuários para grupo Admin');
        return;
      }

      // Preparar dados para atualização (sem cmsPassword)
      const { cmsPassword, ...updateData } = editFormData;
      
      await apiService.updateReseller(selectedReseller.id.toString(), updateData);

      // Se foi fornecida uma nova senha do CMS, alterar separadamente
      if (cmsPassword && cmsPassword.trim()) {
        if (cmsPassword.length < 6) {
          showError('Erro', 'Senha do CMS deve ter pelo menos 6 caracteres');
          return;
        }
        try {
          await apiService.changeCmsPassword(selectedReseller.id, cmsPassword);
          showSuccess('Sucesso', 'Senha do CMS alterada com sucesso');
        } catch (cmsError: any) {
          console.error('Erro ao alterar senha do CMS:', cmsError);
          // Não falhar a atualização do revendedor se apenas a senha do CMS falhar
          showError('Aviso', 'Revendedor atualizado, mas houve erro ao alterar senha do CMS: ' + (cmsError.response?.data?.error || cmsError.message));
        }
      }

      // Mostrar toast de sucesso
      showSuccess(
        'Revendedor Atualizado',
        `As alterações do revendedor "${selectedReseller.username}" foram salvas com sucesso!`
      );

      // Recarregar lista
      loadResellers();
      setShowEditModal(false);
    } catch (err) {
      showError(
        'Erro ao Atualizar',
        err instanceof Error ? err.message : 'Erro ao atualizar revendedor'
      );
    }
  };

  // Função para gerenciar créditos
  const updateCredits = async () => {
    try {
      if (!selectedReseller) {
        throw new Error('Revendedor não selecionado');
      }

      const amount = Number(creditsFormData.amount);
      await apiService.changeResellerCredits(selectedReseller.id.toString(), amount);

      // Mostrar toast de sucesso
      showSuccess(
        'Créditos Atualizados',
        `${amount > 0 ? 'Adicionados' : 'Removidos'} ${Math.abs(amount)} crédito(s) ${amount > 0 ? 'para' : 'de'} "${selectedReseller.username}"`
      );

      // Recarregar lista
      loadResellers();
      setShowCreditsModal(false);
    } catch (err) {
      showError(
        'Erro ao Atualizar Créditos',
        err instanceof Error ? err.message : 'Erro ao atualizar créditos'
      );
    }
  };

  // Obter ícone do grupo
  const getGroupIcon = (reseller: Reseller) => {
    if (reseller.group?.is_admin === 1) {
      return <Shield className="w-4 h-4 text-red-500" />;
    } else if (reseller.group?.is_reseller === 1) {
      return <BarChart3 className="w-4 h-4 text-green-500" />;
    } else {
      return <Users className="w-4 h-4 text-gray-500" />;
    }
  };

  // Obter nome do grupo
  const getGroupName = (reseller: Reseller) => {
    const groupId = reseller.member_group_id || reseller.group?.group_id;
    switch (groupId) {
      case 1: return 'Administrador';
      case 2: return 'Sócio';
      case 3: return 'Master';
      case 4: return 'Revendedor';
      case 6: return 'Sub-revendedor';
      default: return reseller.group?.group_name || 'Sem Grupo';
    }
  };

  // Obter cor do grupo
  const getGroupColor = (reseller: Reseller) => {
    const groupId = reseller.member_group_id || reseller.group?.group_id;
    switch (groupId) {
      case 1: return 'bg-red-100 text-red-800 border-red-200';
      case 2: return 'bg-purple-100 text-purple-800 border-purple-200';
      case 3: return 'bg-blue-100 text-blue-800 border-blue-200';
      case 4: return 'bg-green-100 text-green-800 border-green-200';
      case 6: return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Obter cor do status
  const getStatusColor = (status: number) => {
    switch (status) {
      case 1: return 'bg-green-100 text-green-800';
      case 0: return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Obter texto do status
  const getStatusText = (status: number) => {
    switch (status) {
      case 1: return 'Ativo';
      case 0: return 'Inativo';
      default: return 'Desconhecido';
    }
  };

  // Obter opções de grupo disponíveis
  const getGroupOptions = () => {
    const baseOptions = [
      { value: 2, label: 'Sócio' },
      { value: 3, label: 'Master' },
      { value: 4, label: 'Revendedor' },
      { value: 6, label: 'Sub-revendedor' }
    ];

    // Apenas admins podem criar/editar outros admins
    if (isAdmin()) {
      baseOptions.unshift({ value: 1, label: 'Administrador' });
    }

    return baseOptions;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Erro ao carregar revendedores</h3>
            <div className="mt-2 text-sm text-red-700">
              <p>{error}</p>
            </div>
            <div className="mt-4">
              <Button onClick={loadResellers} variant="outline" size="sm">
                Tentar novamente
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Revendedores</h1>
          <p className="text-sm sm:text-base text-gray-600">
            {isAdmin() 
              ? 'Gerencie todos os revendedores do sistema'
              : 'Gerencie seus revendedores'
            }
          </p>
        </div>
        <Button className="flex items-center gap-2 w-full sm:w-auto" onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">Novo Revendedor</span>
          <span className="sm:hidden">Novo</span>
        </Button>
      </div>

      {/* Filtros */}
      <Card className="overflow-visible">
        <CardContent className="p-6 overflow-visible">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <label className="text-xs font-medium text-gray-700">
                  {searchMode === 'normal' ? 'Buscar por nome, email ou username' : 'Filtrar por Owner (Master)'}
                </label>
                <button
                  type="button"
                  onClick={handleSearchModeToggle}
                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                  title={searchMode === 'normal' ? 'Alternar para filtrar por Owner' : 'Alternar para busca normal'}
                >
                  <Filter className="w-3 h-3" />
                  {searchMode === 'normal' ? 'Filtrar por Owner' : 'Busca Normal'}
                </button>
              </div>
              <div className="relative z-50">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 z-10" />
                <Input
                  ref={searchInputRef}
                  placeholder={searchMode === 'normal' 
                    ? "Buscar por nome, email ou username..."
                    : "Digite para buscar revendedor (Owner)..."
                  }
                  value={searchMode === 'normal' ? searchTerm : ownerNameFilter}
                  onChange={searchMode === 'normal' 
                    ? (e) => setSearchTerm(e.target.value)
                    : handleOwnerNameChange
                  }
                  className="pl-10 pr-10"
                  onFocus={() => {
                    if (searchMode === 'owner' && ownerNameFilter.length >= 2) {
                      setShowOwnerSuggestions(true);
                    }
                  }}
                  onBlur={(e) => {
                    // Verificar se o blur não foi causado por clique nas sugestões
                    const relatedTarget = e.relatedTarget as HTMLElement;
                    if (!relatedTarget || !relatedTarget.closest('.owner-suggestions-dropdown')) {
                      setTimeout(() => setShowOwnerSuggestions(false), 200);
                    }
                  }}
                />
                {(searchMode === 'normal' ? searchTerm : ownerNameFilter) && (
                  <button
                    type="button"
                    onClick={() => {
                      if (searchMode === 'normal') {
                        setSearchTerm('');
                      } else {
                        setOwnerNameFilter('');
                        setOwnerIdFilter('');
                        setShowOwnerSuggestions(false);
                      }
                    }}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10"
                    title="Limpar busca"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                {isSearching && (
                  <div className="absolute right-10 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  </div>
                )}
                
                {/* Dropdown de sugestões de owners */}
                {searchMode === 'owner' && showOwnerSuggestions && ownerSuggestions.length > 0 && (
                  <div className="owner-suggestions-dropdown absolute z-[9999] w-full mt-1 bg-white border border-gray-300 rounded-md shadow-xl max-h-60 overflow-y-auto">
                    <div className="py-1">
                      {ownerSuggestions.map((owner) => (
                        <button
                          key={owner.id}
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleSelectOwner(owner.id, owner.username);
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault(); // Prevenir blur do input
                          }}
                          className="w-full text-left px-4 py-2.5 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none text-sm text-gray-900 transition-colors border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-gray-900">{owner.username}</div>
                          <div className="text-xs text-gray-500 mt-0.5">ID: {owner.id}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="w-full sm:w-auto flex items-end">
              <div className="w-full sm:w-auto">
                <label className="block text-xs font-medium text-gray-700 mb-1.5 sm:hidden">
                  Status
                </label>
                <select
                  value={filterStatus}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm"
                >
                  <option value="all">Todos os status</option>
                  <option value="1">Ativos</option>
                  <option value="0">Inativos</option>
                  <option value="2">Suspensos</option>
                </select>
              </div>
            </div>
          </div>
          {isSearching && (
            <div className="mt-2 text-sm text-gray-500">
              Buscando revendedores...
            </div>
          )}
        </CardContent>
      </Card>

      {/* Estatísticas */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 lg:gap-4">
        <Card>
          <CardContent className="p-3 lg:p-4">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                <Users className="w-4 h-4 lg:w-6 lg:h-6 text-blue-600" />
              </div>
              <div className="ml-3 lg:ml-4 min-w-0 flex-1">
                <p className="text-xs lg:text-sm font-medium text-gray-600">Total</p>
                <p className="text-lg lg:text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 lg:p-4">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                <Users className="w-4 h-4 lg:w-6 lg:h-6 text-green-600" />
              </div>
              <div className="ml-3 lg:ml-4 min-w-0 flex-1">
                <p className="text-xs lg:text-sm font-medium text-gray-600">Ativos</p>
                <p className="text-lg lg:text-2xl font-bold text-gray-900">{stats.active}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 lg:p-4">
            <div className="flex items-center">
              <div className="p-2 bg-gray-100 rounded-lg flex-shrink-0">
                <Users className="w-4 h-4 lg:w-6 lg:h-6 text-gray-600" />
              </div>
              <div className="ml-3 lg:ml-4 min-w-0 flex-1">
                <p className="text-xs lg:text-sm font-medium text-gray-600">Inativos</p>
                <p className="text-lg lg:text-2xl font-bold text-gray-900">{stats.inactive}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 lg:p-4">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg flex-shrink-0">
                <Users className="w-4 h-4 lg:w-6 lg:h-6 text-red-600" />
              </div>
              <div className="ml-3 lg:ml-4 min-w-0 flex-1">
                <p className="text-xs lg:text-sm font-medium text-gray-600">Suspensos</p>
                <p className="text-lg lg:text-2xl font-bold text-gray-900">{stats.suspended}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lista de Revendedores */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Revendedores</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredResellers.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum revendedor encontrado</h3>
              <p className="text-gray-600">
                {(searchMode === 'normal' ? searchTerm : ownerIdFilter) || filterStatus !== 'all'
                  ? 'Tente ajustar os filtros de busca'
                  : 'Comece criando seu primeiro revendedor'
                }
              </p>
            </div>
          ) : (
          <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Usuário
                    </th>
                    <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                      Grupo
                    </th>
                    <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                      Master
                    </th>
                    <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                      Créditos
                    </th>
                    <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                      Painel
                    </th>
                    <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden xl:table-cell">
                      Último Login
                    </th>
                    <th className="px-3 lg:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                </tr>
              </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredResellers.map((reseller) => (
                    <tr key={reseller.id} className="hover:bg-gray-50">
                      <td className="px-3 lg:px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8 lg:h-10 lg:w-10">
                            <div className="h-8 w-8 lg:h-10 lg:w-10 rounded-full bg-gray-300 flex items-center justify-center">
                              <span className="text-xs lg:text-sm font-medium text-gray-700">
                                {reseller.username ? reseller.username.charAt(0).toUpperCase() : 'U'}
                              </span>
                            </div>
                          </div>
                          <div className="ml-3 lg:ml-4 min-w-0 flex-1">
                            <div className="text-sm lg:text-base font-medium text-gray-900 truncate">
                              {reseller.username || 'Nome não disponível'}
                            </div>
                            <div className="text-xs lg:text-sm text-gray-500 truncate">
                              {reseller.email || 'Email não disponível'}
                            </div>
                            {/* Mostrar informações extras em mobile */}
                            <div className="sm:hidden space-y-1 mt-1">
                              <div className="flex items-center">
                                {getGroupIcon(reseller)}
                                <Badge className={`ml-1 text-xs ${getGroupColor(reseller)} border`}>
                                  {getGroupName(reseller)}
                                </Badge>
                              </div>
                              <p className="text-xs text-gray-600">
                                Master: {reseller.owner?.username || reseller.owner_username || 'N/A'}
                              </p>
                              <p className="text-xs text-gray-600">
                                Créditos: {(reseller.credits || 0).toLocaleString()}
                              </p>
                            </div>
                          </div>
                      </div>
                    </td>
                      <td className="px-3 lg:px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                        <div className="flex items-center">
                          {getGroupIcon(reseller)}
                          <Badge className={`ml-2 text-xs ${getGroupColor(reseller)} border`}>
                            {getGroupName(reseller)}
                          </Badge>
                      </div>
                    </td>
                      <td className="px-3 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900 hidden md:table-cell">
                        {reseller.owner?.username || reseller.owner_username || 'N/A'}
                      </td>
                      <td className="px-3 lg:px-6 py-4 whitespace-nowrap">
                        <Badge className={`${getStatusColor(reseller.status)} text-xs lg:text-sm`}>
                          {getStatusText(reseller.status)}
                        </Badge>
                      </td>
                      <td className="px-3 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900 hidden md:table-cell">
                        {(reseller.credits || 0).toLocaleString()}
                      </td>
                      <td className="px-3 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900 hidden lg:table-cell">
                        {reseller.panel || 'N/A'}
                    </td>
                      <td className="px-3 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden xl:table-cell">
                        {reseller.last_login 
                          ? formatDateOnly(Number(reseller.last_login) * 1000)
                          : 'Nunca'
                        }
                    </td>
                      <td className="px-3 lg:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-1 lg:space-x-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleViewReseller(reseller)}
                            title="Visualizar"
                            className="h-8 w-8 lg:h-10 lg:w-10 p-0"
                          >
                            <Eye className="w-6 h-6 lg:w-8 lg:h-8" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleEditReseller(reseller)}
                            title="Editar"
                            className="h-8 w-8 lg:h-10 lg:w-10 p-0"
                          >
                            <Edit className="w-3 h-3 lg:w-4 lg:h-4" />
                        </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleCreditsReseller(reseller)}
                            title="Créditos (Adicionar/Remover)"
                            className="h-8 w-8 lg:h-10 lg:w-10 p-0 border-green-200 bg-green-50 text-green-700 hover:bg-green-100 hover:border-green-300"
                          >
                            <DollarSign className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
                        </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleBlockReseller(reseller)}
                            title={Number(reseller.status) === 1 ? "Bloquear" : "Desbloquear"}
                            className={`h-8 w-8 lg:h-10 lg:w-10 p-0 ${Number(reseller.status) === 1 ? "text-yellow-600 hover:text-yellow-700" : "text-blue-600 hover:text-blue-700"}`}
                          >
                            {Number(reseller.status) === 1 ? <Lock className="w-3 h-3 lg:w-4 lg:h-4" /> : <Unlock className="w-3 h-3 lg:w-4 lg:h-4" />}
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleDeleteReseller(reseller)}
                            title="Deletar"
                            className="h-8 w-8 lg:h-10 lg:w-10 p-0 text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-3 h-3 lg:w-4 lg:h-4" />
                          </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </CardContent>
      </Card>

      {/* Controles de Paginação */}
      {pagination.total_pages > 1 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Mostrando {((pagination.current_page - 1) * pagination.per_page) + 1} a {Math.min(pagination.current_page * pagination.per_page, pagination.total)} de {pagination.total} resultados
              </div>
              
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.current_page - 1)}
                  disabled={!pagination.has_prev}
                >
                  Anterior
                </Button>
                
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                    const page = i + 1;
                    const isActive = page === pagination.current_page;
                    
                    return (
                      <Button
                        key={page}
                        variant={isActive ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(page)}
                        className={isActive ? "bg-blue-600 text-white" : ""}
                      >
                        {page}
                      </Button>
                    );
                  })}
                  
                  {pagination.total_pages > 5 && (
                    <>
                      <span className="text-gray-500">...</span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.total_pages)}
                      >
                        {pagination.total_pages}
                      </Button>
                    </>
                  )}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.current_page + 1)}
                  disabled={!pagination.has_next}
                >
                  Próximo
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modal de Criação de Revendedor */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Criar Novo Revendedor</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Username *</label>
                <Input 
                  type="text" 
                  value={createFormData.username}
                  onChange={(e) => setCreateFormData({...createFormData, username: e.target.value})}
                  className="w-full"
                  placeholder="Nome de usuário único"
                  disabled={createLoading}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <Input 
                  type="email" 
                  value={createFormData.email}
                  onChange={(e) => setCreateFormData({...createFormData, email: e.target.value})}
                  className="w-full"
                  placeholder="email@exemplo.com (opcional)"
                  disabled={createLoading}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Senha *</label>
                <Input 
                  type="password" 
                  value={createFormData.password}
                  onChange={(e) => setCreateFormData({...createFormData, password: e.target.value})}
                  className="w-full"
                  placeholder="Mínimo 6 caracteres"
                  disabled={createLoading}
                />
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Créditos</label>
                <Input 
                  type="number" 
                  value={createFormData.credits}
                  onChange={(e) => setCreateFormData({...createFormData, credits: Number(e.target.value)})}
                  className="w-full"
                  placeholder="0"
                  disabled={createLoading}
                />
              </div>
            </div>

            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium mb-2">Grupo *</label>
                <select 
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={createFormData.member_group_id}
                  onChange={(e) => setCreateFormData({...createFormData, member_group_id: Number(e.target.value)})}
                  disabled={createLoading}
                >
                  {getGroupOptions().map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Selecione o grupo de usuário para o novo revendedor
                </p>
              </div>
            </div>


            {createError && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{createError}</p>
              </div>
            )}
            
            <div className="flex justify-end space-x-2 mt-6">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowCreateModal(false);
                  setCreateFormData({ username: '', email: '', member_group_id: 4, password: '', credits: 0, reseller_dns: '' });
                  setCreateError(null);
                }}
                disabled={createLoading}
              >
                Cancelar
              </Button>
              <Button 
                onClick={createReseller}
                disabled={createLoading || !createFormData.username || !createFormData.password}
              >
                {createLoading ? 'Criando...' : 'Criar Revendedor'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Visualização - Layout moderno e organizado */}
      {showViewModal && selectedReseller && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header do modal */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gradient-to-r from-slate-50 to-white">
              <div className="flex items-center gap-4">
                <div className="h-14 w-14 rounded-xl bg-blue-100 flex items-center justify-center">
                  <User className="w-7 h-7 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{selectedReseller.username}</h3>
                  <p className="text-sm text-gray-500">
                    {getGroupName(selectedReseller)} • ID: {selectedReseller.id}
                  </p>
                  <div className="mt-1">
                    <Badge className={`${getStatusColor(selectedReseller.status)} text-xs`}>
                      {getStatusText(selectedReseller.status)}
                    </Badge>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowViewModal(false)}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                aria-label="Fechar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Conteúdo scrollável */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Card destaque: Clientes ativos na árvore */}
              <div className="rounded-xl border-2 border-blue-100 bg-gradient-to-br from-blue-50 to-indigo-50 p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2.5 rounded-lg bg-blue-500/10">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">Clientes ativos na árvore</h4>
                    <p className="text-xs text-gray-600">
                      Linhas ativas vinculadas ao revendedor e sub-revendedores
                    </p>
                  </div>
                  {viewModalActiveClientsData && (
                    <label className="flex items-center gap-2 cursor-pointer shrink-0">
                      <input
                        type="checkbox"
                        checked={viewModalIncludeTrials}
                        onChange={(e) => setViewModalIncludeTrials(e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-xs text-gray-600">Incluir testes</span>
                    </label>
                  )}
                </div>
                <div className="mt-3 space-y-3">
                  {viewModalLoadingClients ? (
                    <div className="flex items-center gap-2 text-gray-500">
                      <div className="w-5 h-5 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin" />
                      <span className="text-sm">Carregando...</span>
                    </div>
                  ) : viewModalActiveClientsData ? (
                    <>
                      <p className="text-3xl font-bold text-blue-700">
                        {(viewModalIncludeTrials
                          ? viewModalActiveClientsData.active_clients_tree_with_trials
                          : viewModalActiveClientsData.active_clients_tree
                        ).toLocaleString('pt-BR')}
                        <span className="text-sm font-normal text-gray-500 ml-1">total</span>
                      </p>
                      <div className="flex flex-wrap gap-4 pt-2 border-t border-blue-100">
                        <div>
                          <span className="text-xs text-gray-500">IPTV (system=1): </span>
                          <span className="font-semibold text-gray-800">
                            {(viewModalIncludeTrials
                              ? viewModalActiveClientsData.active_iptv_with_trials
                              : viewModalActiveClientsData.active_iptv
                            ).toLocaleString('pt-BR')}
                          </span>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500">P2P (system=2): </span>
                          <span className="font-semibold text-gray-800">
                            {(viewModalIncludeTrials
                              ? viewModalActiveClientsData.active_p2p_with_trials
                              : viewModalActiveClientsData.active_p2p
                            ).toLocaleString('pt-BR')}
                          </span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-amber-600">Não foi possível carregar</p>
                  )}
                </div>
              </div>

              {/* Grid de informações */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Informações básicas */}
                <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-5">
                  <h4 className="font-medium text-gray-900 flex items-center gap-2 mb-4">
                    <Hash className="w-4 h-4 text-gray-500" />
                    Informações básicas
                  </h4>
                  <dl className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-gray-600">Email</dt>
                      <dd className="font-medium text-gray-900 truncate max-w-[180px]" title={selectedReseller.email || ''}>
                        {selectedReseller.email || '—'}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-600">IP</dt>
                      <dd className="font-medium text-gray-900">{selectedReseller.ip || '—'}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-600">Créditos</dt>
                      <dd className="font-medium text-gray-900">{(selectedReseller.credits || 0).toLocaleString('pt-BR')}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-600">Master</dt>
                      <dd className="font-medium text-gray-900">
                        {selectedReseller.owner?.username || selectedReseller.owner_username || '—'}
                      </dd>
                    </div>
                  </dl>
                </div>

                {/* Configurações do sistema */}
                <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-5">
                  <h4 className="font-medium text-gray-900 flex items-center gap-2 mb-4">
                    <Globe className="w-4 h-4 text-gray-500" />
                    Configurações do sistema
                  </h4>
                  <dl className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-gray-600">Painel</dt>
                      <dd className="font-medium text-gray-900 truncate max-w-[160px]" title={selectedReseller.panel || ''}>
                        {selectedReseller.panel || '—'}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-600">DNS</dt>
                      <dd className="font-medium text-gray-900 truncate max-w-[160px]" title={selectedReseller.reseller_dns || ''}>
                        {selectedReseller.reseller_dns || '—'}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-600">Registro</dt>
                      <dd className="font-medium text-gray-900">
                        {selectedReseller.date_registered
                          ? new Date(Number(selectedReseller.date_registered) * 1000).toLocaleDateString('pt-BR')
                          : '—'}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-600">Último login</dt>
                      <dd className="font-medium text-gray-900">
                        {selectedReseller.last_login
                          ? new Date(Number(selectedReseller.last_login) * 1000).toLocaleDateString('pt-BR')
                          : 'Nunca'}
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>

              {/* Notas */}
              {selectedReseller.notes && (
                <div className="rounded-xl border border-gray-200 bg-amber-50/30 p-5">
                  <h4 className="font-medium text-gray-900 flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-gray-500" />
                    Notas
                  </h4>
                  <p className="text-sm text-gray-600">{selectedReseller.notes}</p>
                </div>
              )}
            </div>

            {/* Footer com ações */}
            <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-100 bg-gray-50/50">
              <Button
                variant="outline"
                onClick={() => {
                  setShowViewModal(false);
                  handleEditReseller(selectedReseller);
                }}
              >
                <Edit className="w-4 h-4 mr-2" />
                Editar
              </Button>
              <Button onClick={() => setShowViewModal(false)}>
                Fechar
              </Button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && selectedReseller && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4 text-red-600">Confirmar Exclusão</h3>
            <p className="mb-4">
              Tem certeza que deseja deletar o revendedor <strong>{selectedReseller.username}</strong>?
              Esta ação não pode ser desfeita.
            </p>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
                Cancelar
              </Button>
              <Button 
                variant="outline" 
                className="text-red-600 hover:text-red-700"
                onClick={() => deleteReseller(selectedReseller)}
              >
                Deletar
              </Button>
            </div>
          </div>
        </div>
      )}

      {showMigrateModal && selectedReseller && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Migrar Clientes</h3>
            <p className="mb-4">
              O revendedor <strong>{selectedReseller.username}</strong> possui clientes. Selecione um revendedor de destino para migrá-los antes da exclusão.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Destino</label>
                <select 
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={migrateTargetId || ''}
                  onChange={(e) => setMigrateTargetId(Number(e.target.value))}
                >
                  {migrateTargets.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <Button variant="outline" onClick={() => setShowMigrateModal(false)}>
                Cancelar
              </Button>
              <Button onClick={confirmMigrateAndDelete} className="text-red-600 hover:text-red-700">
                Migrar e Excluir
              </Button>
            </div>
          </div>
        </div>
      )}

      {showBlockModal && selectedReseller && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">
              {Number(selectedReseller.status) === 1 ? 'Bloquear' : 'Desbloquear'} Revendedor
            </h3>
            <p className="mb-4">
              Tem certeza que deseja {Number(selectedReseller.status) === 1 ? 'bloquear' : 'desbloquear'} o revendedor <strong>{selectedReseller.username}</strong>?
            </p>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowBlockModal(false)}>
                Cancelar
              </Button>
              <Button 
                variant="outline" 
                className={Number(selectedReseller.status) === 1 ? "text-yellow-600 hover:text-yellow-700" : "text-blue-600 hover:text-blue-700"}
                onClick={() => toggleBlockReseller(selectedReseller, Number(selectedReseller.status) === 1)}
              >
                {Number(selectedReseller.status) === 1 ? 'Bloquear' : 'Desbloquear'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showEditModal && selectedReseller && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Editar Revendedor</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Informações Básicas */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900 border-b pb-2">Informações Básicas</h4>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Username:</label>
                  <Input 
                    type="text" 
                    value={editFormData.username}
                    onChange={(e) => setEditFormData({...editFormData, username: e.target.value})}
                    className="w-full"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Email:</label>
                  <Input 
                    type="email" 
                    value={editFormData.email}
                    onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                    className="w-full"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Nova Senha IPTV (opcional):</label>
                  <Input 
                    type="password" 
                    value={editFormData.password}
                    onChange={(e) => setEditFormData({...editFormData, password: e.target.value})}
                    className="w-full"
                    placeholder="Deixe em branco para manter a senha atual"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Nova Senha do CMS (opcional):</label>
                  <Input 
                    type="password" 
                    value={editFormData.cmsPassword}
                    onChange={(e) => setEditFormData({...editFormData, cmsPassword: e.target.value})}
                    className="w-full"
                    placeholder="Deixe em branco para manter a senha atual do CMS"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Use este campo para alterar a senha de acesso ao CMS caso o revendedor tenha esquecido
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Notas:</label>
                  <textarea 
                    value={editFormData.notes}
                    onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                  />
                </div>
              </div>

              {/* Configurações do Sistema */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900 border-b pb-2">Configurações do Sistema</h4>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Status:</label>
                  <select 
                    value={editFormData.status}
                    onChange={(e) => setEditFormData({...editFormData, status: Number(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={1}>Ativo</option>
                    <option value={0}>Bloqueado</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Grupo:</label>
                  <select 
                    value={editFormData.member_group_id}
                    onChange={(e) => setEditFormData({...editFormData, member_group_id: Number(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {getGroupOptions().map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {editFormData.member_group_id === 1 ? '⚠️ Apenas administradores podem alterar para grupo Admin' : ''}
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Painel:</label>
                  <Input 
                    type="text" 
                    value={editFormData.panel}
                    onChange={(e) => setEditFormData({...editFormData, panel: e.target.value})}
                    className="w-full"
                    placeholder="ex: union, u9, xtream, custom"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">DNS do Revendedor:</label>
                  <Input 
                    type="text" 
                    value={editFormData.reseller_dns}
                    onChange={(e) => setEditFormData({...editFormData, reseller_dns: e.target.value})}
                    className="w-full"
                    placeholder="ex: revendedor.exemplo.com"
                  />
                </div> 
              </div>
            </div>
            
            <div className="flex justify-end space-x-2 mt-6 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowEditModal(false)}>
                Cancelar
              </Button>
              <Button onClick={updateReseller}>
                Salvar Alterações
              </Button>
            </div>
          </div>
        </div>
      )}

      {showCreditsModal && selectedReseller && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Gerenciar Créditos</h3>
            <p className="mb-4">
              Revendedor: <strong>{selectedReseller.username}</strong><br/>
              Créditos atuais: <strong>{(selectedReseller.credits || 0).toLocaleString()}</strong>
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Valor a adicionar/remover:</label>
                <Input 
                  type="number" 
                  placeholder="Digite o valor"
                  value={creditsFormData.amount}
                  onChange={(e) => setCreditsFormData({...creditsFormData, amount: e.target.value})}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Motivo:</label>
                <Input 
                  type="text" 
                  placeholder="Motivo da alteração"
                  value={creditsFormData.reason}
                  onChange={(e) => setCreditsFormData({...creditsFormData, reason: e.target.value})}
                  className="w-full"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <Button variant="outline" onClick={() => setShowCreditsModal(false)}>
                Cancelar
              </Button>
              <Button onClick={updateCredits} className="text-green-600 hover:text-green-700">
                Aplicar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Resellers;